import React, { useContext, useEffect, useMemo, useState } from 'react';
import { cleanObject, isEmptyObject } from '@teko/utils';
import { useSearch, useTenantConfig } from 'hooks';
import { DiscoverySearchProductResponse } from '@teko/discovery/v2';
import { ProductCardProps } from '@teko/types/ecommerce';
import { BuildPcConfig } from '@teko/ecommerce';
import { logger } from '@teko/log';
import { BUILDPC_V3_LOCAL_STORAGE_KEY } from 'constants/index';
import {
  IComputedBuildPCData,
  ILSBuildPCData,
  ILSHardwareData,
  lsBuildPCConfigType,
} from 'types';
import { getLocalConfigs } from 'helpers';
import { useServices } from './ServiceContext';
import { t } from 'i18n';
import { useToast } from './ToastContext';

export interface BuildPCV2ProviderProps {
  children: React.ReactNode;
}

export interface IBuildPCV2ContextState {
  loading?: boolean;
  buildPCData: IComputedBuildPCData;
  buildPCDetailProducts: DiscoverySearchProductResponse<ProductCardProps>;
  changeActiveConfig: (activeIndex: number) => void;
  increaseHardwareQuantity: (hardwareType: string) => void;
  decreaseHardwareQuantity: (hardwareType: string) => void;
  setHardwareQuantity: (hardwareType: string, quantity: number) => void;
  addHardware: (hardwareType: string, hardwareData: ILSHardwareData) => void;
  removeHardware: (hardwareType: string) => void;
  updateCurrentConfig: (newConfig: lsBuildPCConfigType) => void;
  onUpdateConfig: (product: ProductCardProps) => Promise<void>;
  mappedPrintBuildPCData: BuildPcConfig;
  estimatedAmount: number;
}

const initialState: IBuildPCV2ContextState = {
  loading: false,
  buildPCData: {
    configs: [{}, {}, {}],
    activeIndex: 0,
    currentConfig: {},
  },
  buildPCDetailProducts: {
    products: [],
    filters: {},
    pagination: { totalItems: 0, totalPages: 0 },
  },
  changeActiveConfig: () => {},
  increaseHardwareQuantity: () => {},
  decreaseHardwareQuantity: () => {},
  setHardwareQuantity: () => {},
  addHardware: () => {},
  removeHardware: () => {},
  updateCurrentConfig: () => {},
  onUpdateConfig: () => Promise.resolve(),
  mappedPrintBuildPCData: {},
  estimatedAmount: 0,
};

const BuildPCV2Context =
  React.createContext<IBuildPCV2ContextState>(initialState);

export const BuildPCV2Provider: React.FC<BuildPCV2ProviderProps> = ({
  children,
}) => {
  const { discoveryServiceV2 } = useServices();
  const { buildPcMapping } = useTenantConfig();
  const toast = useToast();

  const [lsBuildPCData, setLsBuildPCData] = useState<ILSBuildPCData>(
    getLocalConfigs(),
  );

  const buildPCData = useMemo(() => {
    return {
      ...lsBuildPCData,
      currentConfig: lsBuildPCData.configs[lsBuildPCData.activeIndex],
    };
  }, [lsBuildPCData]);

  // update all local storage config when current config changes
  useEffect(() => {
    localStorage.setItem(
      BUILDPC_V3_LOCAL_STORAGE_KEY,
      JSON.stringify(lsBuildPCData),
    );
  }, [lsBuildPCData]);

  // get all skus from current active config
  const currentConfigSkus = useMemo(() => {
    const skus: string[] = [];
    Object.keys(buildPCData.currentConfig).forEach((configKey) => {
      if (
        !!buildPCData.currentConfig[configKey]?.sku &&
        !skus.includes(buildPCData.currentConfig[configKey].sku)
      ) {
        skus.push(buildPCData.currentConfig[configKey].sku);
      }
    });

    return skus;
  }, [buildPCData.currentConfig]);

  // call api search-skus-v2 to get all current active config skus detail info
  const { result: buildPCDetailProducts, isLoading } = useSearch(
    cleanObject(
      !!currentConfigSkus.length
        ? {
            pageSize: currentConfigSkus.length,
            filter: {
              skus: currentConfigSkus,
            },
          }
        : {},
    ),
  );

  // Check if current active config is empty or not
  const isCurrentConfigEmpty = useMemo(() => {
    return Object.keys(buildPCData.currentConfig).length === 0;
  }, [buildPCData.currentConfig]);

  // Calculate total price of all products in current config
  const estimatedAmount = useMemo(() => {
    if (isCurrentConfigEmpty) {
      return 0;
    }
    return Object.keys(buildPCData.currentConfig).reduce((total, current) => {
      const lsHardware = buildPCData.currentConfig[current];
      if (!!lsHardware && !isEmptyObject(lsHardware)) {
        const hardwareDetail = buildPCDetailProducts.products.find(
          (product) => {
            return product.sku === lsHardware.sku;
          },
        );

        const quantity = lsHardware.quantity || 0;
        const price =
          hardwareDetail?.price?.latestPrice ||
          hardwareDetail?.price?.price ||
          0;
        return total + price * quantity;
      }
      return total;
    }, 0);
  }, [buildPCData.currentConfig, isCurrentConfigEmpty, buildPCDetailProducts]);

  // Map print data to match interface of PrintBuildPc component
  const mappedPrintBuildPCData = useMemo(() => {
    return Object.keys(buildPCData.currentConfig).reduce(
      (result, currentKey) => {
        const lsHardware = buildPCData.currentConfig[currentKey];
        const productData = buildPCDetailProducts.products.find((product) => {
          return product.sku === lsHardware?.sku;
        });
        if (!!productData && !isEmptyObject(productData)) {
          result[currentKey] = {
            product: productData,
            quantity: lsHardware.quantity,
          };
        }
        return result;
      },
      {} as BuildPcConfig,
    );
  }, [buildPCData.currentConfig, buildPCDetailProducts]);

  const changeActiveConfig = (activeIndex: number) => {
    setLsBuildPCData({ ...lsBuildPCData, activeIndex });
  };

  const increaseHardwareQuantity = (hardwareType: string) => {
    setLsBuildPCData({
      ...lsBuildPCData,
      configs: lsBuildPCData.configs.map((config, index) => {
        // check if config is the active config
        if (lsBuildPCData.activeIndex === index) {
          return {
            ...config,
            [hardwareType]: {
              ...config[hardwareType],
              quantity: config[hardwareType].quantity + 1,
            },
          };
        }
        return config;
      }),
    });
  };

  const decreaseHardwareQuantity = (hardwareType: string) => {
    setLsBuildPCData({
      ...lsBuildPCData,
      configs: lsBuildPCData.configs.map((config, index) => {
        // check if config is the active config
        if (lsBuildPCData.activeIndex === index) {
          // decrease the hardware type quantity
          return {
            ...config,
            [hardwareType]: {
              ...config[hardwareType],
              quantity: config[hardwareType].quantity - 1,
            },
          };
        }
        return config;
      }),
    });
  };

  const setHardwareQuantity = (hardwareType: string, quantity: number) => {
    // stop when input quantity is 0
    if (!quantity) {
      return;
    }
    setLsBuildPCData({
      ...lsBuildPCData,
      configs: lsBuildPCData.configs.map((config, index) => {
        // check if config is the active config
        if (lsBuildPCData.activeIndex === index) {
          return {
            ...config,
            [hardwareType]: {
              ...config[hardwareType],
              quantity,
            },
          };
        }
        return config;
      }),
    });
  };

  const addHardware = (hardwareType: string, hardwareData: ILSHardwareData) => {
    setLsBuildPCData({
      ...lsBuildPCData,
      configs: lsBuildPCData.configs.map((config, index) => {
        // check if config is the active config
        if (lsBuildPCData.activeIndex === index) {
          // check if the new hardware is already existed in active config
          if (
            !isEmptyObject(config[hardwareType]) &&
            config[hardwareType].sku === hardwareData.sku
          ) {
            //if yes, increase its quantity
            return {
              ...config,
              [hardwareType]: {
                ...config[hardwareType],
                quantity: config[hardwareType].quantity + 1,
              },
            };
          }
          // if not, update the hardware type with new hardware
          return {
            ...config,
            [hardwareType]: hardwareData,
          };
        }
        return config;
      }),
    });
  };

  const removeHardware = (hardwareType: string) => {
    setLsBuildPCData({
      ...lsBuildPCData,
      configs: lsBuildPCData.configs.map((config, index) => {
        // check if config is the active config
        if (lsBuildPCData.activeIndex === index) {
          const { [hardwareType]: _, ...remainingConfig } = config;
          return remainingConfig;
        }
        return config;
      }),
    });
  };

  const updateCurrentConfig = (newConfig: lsBuildPCConfigType) => {
    // since this function will be used in a component outside the provider
    // buildpc state won't be the same --> we'll use buildpc data from localstorage instead.
    const lsData = getLocalConfigs();
    setLsBuildPCData({
      ...lsData,
      configs: lsData.configs.map((config, index) => {
        // check if config is the active config
        if (lsData.activeIndex === index) {
          // replace with new config
          return newConfig;
        }
        return config;
      }),
    });
  };

  const onUpdateConfig = async (product: ProductCardProps) => {
    if (
      !product.materials?.length ||
      !buildPcMapping ||
      isEmptyObject(buildPcMapping)
    ) {
      toast?.error?.({
        title: t('Sản phẩm này không hỗ trợ chỉnh sửa cấu hình'),
        message: '',
      });
      return;
    }
    try {
      // get all material skus of current product
      const materialSkus = product.materials.map((material) => material.sku);

      // call api search-skus-v2 to get materials categories for mapping
      const data = await discoveryServiceV2.search({
        pageSize: materialSkus.length,
        filter: { skus: materialSkus },
      });

      const materialProducts = data.products;

      // Get all mapping keys from buildPcMapping
      const buildPCMappingKeys =
        !!buildPcMapping && !isEmptyObject(buildPcMapping)
          ? Object.keys(buildPcMapping)
          : [];

      // Create new buildpc config from product's materials & buildpc mapping keys
      const newConfig = buildPCMappingKeys.reduce((config, currentKey) => {
        // Get defined buildpc mapping categories from tenant config
        const currentMappingCategories = buildPcMapping[currentKey].categories;

        materialProducts?.forEach((materialProduct) => {
          // Check if any categories from materialProducts included in current key mapping categories
          const matchedCategory = materialProduct.categories?.find(
            (category) => {
              return currentMappingCategories.find((mappingCategory) => {
                return mappingCategory === category.code;
              });
            },
          );

          // if exists, add material sku & quantity to new config
          if (!!matchedCategory && !!materialProduct.sku) {
            // find material quantity in product's materials by sku
            const matchedMaterial = product.materials?.find(
              (material) => material.sku === materialProduct.sku,
            );
            if (!!matchedMaterial) {
              config[currentKey] = {
                sku: materialProduct.sku,
                quantity: matchedMaterial.quantity,
              };
            }
          }
        });
        return config;
      }, {} as lsBuildPCConfigType);

      if (!isEmptyObject(newConfig)) {
        updateCurrentConfig(newConfig);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  return (
    <BuildPCV2Context.Provider
      value={{
        loading: isLoading,
        buildPCData,
        buildPCDetailProducts,
        changeActiveConfig,
        increaseHardwareQuantity,
        decreaseHardwareQuantity,
        setHardwareQuantity,
        addHardware,
        removeHardware,
        updateCurrentConfig,
        onUpdateConfig,
        mappedPrintBuildPCData,
        estimatedAmount,
      }}
    >
      {children}
    </BuildPCV2Context.Provider>
  );
};

export const useBuildPCV2 = () => {
  const context = useContext(BuildPCV2Context);
  if (!context) {
    throw new Error(
      'useBuildPCV2 must be inside a BuildPCV2Provider with a state value',
    );
  }
  return context;
};
